import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

export const NOTIFICATIONS_CHANGED = "family:notifications-changed";
export const POLL_MS = 60000;

export function announceNotificationsChanged(unread) {
  window.dispatchEvent(new CustomEvent(NOTIFICATIONS_CHANGED, { detail: { unread_count: unread } }));
}

function apiMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  return typeof detail === "string" && detail ? detail : fallback;
}

/** Conteggio non lette: subito, ogni 60 s solo a scheda visibile, al ritorno sulla scheda e dopo ogni modifica. */
export function useUnreadCount(api, ospiteId, enabled = true) {
  const [count, setCount] = useState(null);
  const alive = useRef(true);
  const refresh = useCallback(async () => {
    if (!enabled || !ospiteId) return;
    try {
      const { data } = await api.get(`/ospiti/${ospiteId}/notifiche/conteggio`);
      if (alive.current) setCount(Number(data?.unread_count) || 0);
    } catch {
      /* il conteggio è un di più: in caso di errore resta il valore precedente */
    }
  }, [api, ospiteId, enabled]);

  useEffect(() => {
    alive.current = true;
    setCount(null);
    if (!enabled || !ospiteId) return undefined;
    refresh();
    const tick = () => { if (!document.hidden) refresh(); };
    const timer = window.setInterval(tick, POLL_MS);
    const onVisible = () => { if (!document.hidden) refresh(); };
    const onChanged = (event) => {
      if (typeof event.detail?.unread_count === "number") setCount(event.detail.unread_count);
      else refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener(NOTIFICATIONS_CHANGED, onChanged);
    return () => {
      alive.current = false;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(NOTIFICATIONS_CHANGED, onChanged);
    };
  }, [refresh, enabled, ospiteId]);

  return { count, refresh };
}

/** Elenco paginato con stato di lettura (usato dal pannello e dalla pagina Notifiche). */
export function useNotificationList(api, ospiteId, { pageSize = 10, onlyUnread = false, active = true } = {}) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [unread, setUnread] = useState(0);
  const offset = useRef(0);
  const request = useRef(0);

  const fetchPage = useCallback(async (start) => {
    const { data } = await api.get(`/ospiti/${ospiteId}/notifiche`, {
      params: { limit: pageSize, offset: start, ...(onlyUnread ? { solo_non_lette: true } : {}) },
    });
    return data;
  }, [api, ospiteId, pageSize, onlyUnread]);

  const load = useCallback(async () => {
    if (!ospiteId) return;
    const mine = ++request.current;
    setStatus("loading");
    setError("");
    try {
      const data = await fetchPage(0);
      if (mine !== request.current) return;
      offset.current = data.next_offset ?? data.items.length;
      setItems(data.items || []);
      setHasMore(Boolean(data.has_more));
      setUnread(data.unread_count || 0);
      setStatus("ready");
      announceNotificationsChanged(data.unread_count || 0);
    } catch (err) {
      if (mine !== request.current) return;
      setError(apiMessage(err, "Non è stato possibile caricare le notifiche"));
      setStatus("error");
    }
  }, [fetchPage, ospiteId]);

  useEffect(() => {
    if (active) load();
    return () => { request.current += 1; };
  }, [active, load]);

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const data = await fetchPage(offset.current);
      offset.current = data.next_offset ?? offset.current + data.items.length;
      setItems((prev) => [...prev, ...data.items.filter((n) => !prev.some((p) => p.id === n.id))]);
      setHasMore(Boolean(data.has_more));
      setUnread(data.unread_count || 0);
    } catch (err) {
      toast.error(apiMessage(err, "Non è stato possibile caricare altre notifiche"));
    } finally {
      setLoadingMore(false);
    }
  }, [fetchPage]);

  const markRead = useCallback(async (id) => {
    const target = items.find((n) => n.id === id);
    if (!target || target.letta) return;
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, letta: true } : n)));
    setUnread((value) => Math.max(0, value - 1));
    try {
      const { data } = await api.post(`/ospiti/${ospiteId}/notifiche/${encodeURIComponent(id)}/letta`);
      setUnread(data.unread_count);
      announceNotificationsChanged(data.unread_count);
    } catch (err) {
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, letta: false } : n)));
      setUnread((value) => value + 1);
      toast.error(apiMessage(err, "Non è stato possibile segnare la notifica come letta"));
    }
  }, [api, ospiteId, items]);

  const markAllRead = useCallback(async () => {
    const before = items;
    setItems((prev) => prev.map((n) => ({ ...n, letta: true })));
    setUnread(0);
    try {
      await api.post(`/ospiti/${ospiteId}/notifiche/tutte-lette`);
      announceNotificationsChanged(0);
      toast.success("Tutte le notifiche sono state segnate come lette");
    } catch (err) {
      setItems(before);
      setUnread(before.filter((n) => !n.letta).length);
      toast.error(apiMessage(err, "Non è stato possibile segnare le notifiche come lette"));
    }
  }, [api, ospiteId, items]);

  return { items, status, error, hasMore, loadingMore, unread, load, loadMore, markRead, markAllRead };
}
